/**
 * WooCommerce Integration Setup Component
 * Provides a user-friendly interface for setting up WooCommerce integration
 */

import React, { useState } from 'react';
import { WooCommerceIntegrationFactory, WooCommerceConnectionParams } from '../../services/integrations/ecommerce';
import { SelectField } from "components/ui/select-field";

interface WooCommerceIntegrationSetupProps {
  onComplete: (integration: any) => void;
  onCancel: () => void;
  initialConfig?: Partial<WooCommerceConnectionParams>;
}

interface FormData extends WooCommerceConnectionParams {
  testConnection: boolean;
}

interface ValidationErrors {
  [key: string]: string;
}

interface ConnectionTest {
  status: 'idle' | 'testing' | 'success' | 'error';
  message?: string;
  details?: any;
}

export const WooCommerceIntegrationSetup: React.FC<WooCommerceIntegrationSetupProps> = ({
  onComplete,
  onCancel,
  initialConfig = {}
}) => {
  const [formData, setFormData] = useState<FormData>({
    siteUrl: initialConfig.siteUrl || '',
    consumerKey: initialConfig.consumerKey || '',
    consumerSecret: initialConfig.consumerSecret || '',
    apiVersion: initialConfig.apiVersion || 'v3',
    useSSL: initialConfig.useSSL !== false,
    timeout: initialConfig.timeout || 30000,
    webhookEndpoint: initialConfig.webhookEndpoint || '',
    webhookSecret: initialConfig.webhookSecret || '',
    testConnection: false
  });

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [connectionTest, setConnectionTest] = useState<ConnectionTest>({ status: 'idle' });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validate form data
  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    
    if (!formData.siteUrl) {
      errors.siteUrl = 'Site URL is required';
    } else if (!isValidUrl(formData.siteUrl)) {
      errors.siteUrl = 'Please enter a valid URL';
    }

    if (!formData.consumerKey) {
      errors.consumerKey = 'Consumer key is required';
    } else if (formData.consumerKey.length < 10) {
      errors.consumerKey = 'Consumer key appears to be too short';
    }

    if (!formData.consumerSecret) {
      errors.consumerSecret = 'Consumer secret is required';
    } else if (formData.consumerSecret.length < 10) {
      errors.consumerSecret = 'Consumer secret appears to be too short';
    }

    if (formData.webhookEndpoint && !isValidUrl(formData.webhookEndpoint)) {
      errors.webhookEndpoint = 'Please enter a valid webhook URL';
    }

    if (formData.timeout < 1000 || formData.timeout > 300000) {
      errors.timeout = 'Timeout must be between 1 and 300 seconds';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Test connection to WooCommerce
  const testConnection = async () => {
    if (!validateForm()) {
      return;
    }

    setConnectionTest({ status: 'testing', message: 'Testing connection...' });

    try {
      // First test site accessibility
      const siteTest = await WooCommerceIntegrationFactory.testSiteAccessibility(formData.siteUrl);
      
      if (!siteTest.accessible) {
        setConnectionTest({
          status: 'error',
          message: `Site is not accessible: ${siteTest.error}`
        });
        return;
      }

      if (!siteTest.isWordPress) {
        setConnectionTest({
          status: 'error',
          message: 'Site does not appear to be a WordPress site'
        });
        return;
      }

      if (!siteTest.isWooCommerce) {
        setConnectionTest({
          status: 'error',
          message: 'WooCommerce plugin not detected or REST API not accessible'
        });
        return;
      }

      // Test actual API connection
      const integration = WooCommerceIntegrationFactory.create(formData);
      const testResult = await integration.testConnection();

      if (testResult.success) {
        setConnectionTest({
          status: 'success',
          message: testResult.message,
          details: testResult.details
        });
      } else {
        setConnectionTest({
          status: 'error',
          message: testResult.message || 'Connection test failed'
        });
      }
    } catch (error) {
      setConnectionTest({
        status: 'error',
        message: error instanceof Error ? error.message : 'Connection test failed'
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const integration = WooCommerceIntegrationFactory.create(formData);
      
      // Test connection if not already tested
      if (connectionTest.status !== 'success') {
        setConnectionTest({ status: 'testing', message: 'Testing connection...' });

        try {
          // First test site accessibility
          const siteTest = await WooCommerceIntegrationFactory.testSiteAccessibility(formData.siteUrl);
          
          if (!siteTest.accessible || !siteTest.isWordPress || !siteTest.isWooCommerce) {
            setConnectionTest({
              status: 'error',
              message: !siteTest.accessible ? `Site is not accessible: ${siteTest.error}` :
                      !siteTest.isWordPress ? 'Site does not appear to be a WordPress site' :
                      'WooCommerce plugin not detected or REST API not accessible'
            });
            setIsSubmitting(false);
            return;
          }

          // Test actual API connection
          const testResult = await integration.testConnection();
          if (!testResult.success) {
            setConnectionTest({
              status: 'error',
              message: testResult.message || 'Connection test failed'
            });
            setIsSubmitting(false);
            return;
          }

          setConnectionTest({
            status: 'success',
            message: testResult.message,
            details: testResult.details
          });
        } catch (error) {
          setConnectionTest({
            status: 'error',
            message: error instanceof Error ? error.message : 'Connection test failed'
          });
          setIsSubmitting(false);
          return;
        }
      }

      onComplete(integration);
    } catch (error) {
      setValidationErrors({
        general: error instanceof Error ? error.message : 'Failed to create integration'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Reset connection test when credentials change
    if (['siteUrl', 'consumerKey', 'consumerSecret'].includes(field)) {
      setConnectionTest({ status: 'idle' });
    }
  };

  // Generate credentials URL
  const generateCredentialsUrl = () => {
    if (formData.siteUrl) {
      return WooCommerceIntegrationFactory.generateCredentialsUrl(formData.siteUrl);
    }
    return null;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const credentialsUrl = generateCredentialsUrl();

  return (
    <div className="max-w-2xl mx-auto p-6 bg-surface rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-content dark:text-gray-100 mb-2">
          Connect WooCommerce Store
        </h2>
        <p className="text-content-secondary dark:text-gray-400">
          Set up integration with your WooCommerce store to sync products, customers, and orders.
        </p>
      </div>

      {validationErrors.general && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-md">
          <p className="text-red-800 dark:text-red-300">{validationErrors.general}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Site URL */}
        <div>
          <label htmlFor="siteUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Store URL *
          </label>
          <input
            type="url"
            id="siteUrl"
            value={formData.siteUrl}
            onChange={(e) => handleInputChange('siteUrl', e.target.value)}
            placeholder="https://yourstore.com"
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              validationErrors.siteUrl ? 'border-red-300 dark:border-red-800/50' : 'border-line-strong'
            }`}
          />
          {validationErrors.siteUrl && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-300">{validationErrors.siteUrl}</p>
          )}
          <p className="mt-1 text-sm text-content-muted">
            The full URL of your WooCommerce store
          </p>
        </div>

        {/* Consumer Key */}
        <div>
          <label htmlFor="consumerKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Consumer Key *
          </label>
          <input
            type="text"
            id="consumerKey"
            value={formData.consumerKey}
            onChange={(e) => handleInputChange('consumerKey', e.target.value)}
            placeholder="ck_..."
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              validationErrors.consumerKey ? 'border-red-300 dark:border-red-800/50' : 'border-line-strong'
            }`}
          />
          {validationErrors.consumerKey && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-300">{validationErrors.consumerKey}</p>
          )}
        </div>

        {/* Consumer Secret */}
        <div>
          <label htmlFor="consumerSecret" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Consumer Secret *
          </label>
          <input
            type="password"
            id="consumerSecret"
            value={formData.consumerSecret}
            onChange={(e) => handleInputChange('consumerSecret', e.target.value)}
            placeholder="cs_..."
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              validationErrors.consumerSecret ? 'border-red-300 dark:border-red-800/50' : 'border-line-strong'
            }`}
          />
          {validationErrors.consumerSecret && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-300">{validationErrors.consumerSecret}</p>
          )}
        </div>

        {/* Generate API Keys Link */}
        {credentialsUrl && (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800/50 rounded-md">
            <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
              Need to generate API keys?
            </p>
            <a
              href={credentialsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-300"
            >
              Open WooCommerce API Keys Settings
              <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M7 7l10 10M17 7v10" />
              </svg>
            </a>
          </div>
        )}

        {/* Advanced Settings */}
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center text-sm text-content-secondary hover:text-gray-800 dark:hover:text-gray-200"
          >
            <svg
              className={`mr-2 w-4 h-4 transform transition-transform ${
                showAdvanced ? 'rotate-90' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            Advanced Settings
          </button>

          {showAdvanced && (
            <div className="mt-4 space-y-4 pl-6 border-l-2 border-line">
              {/* API Version */}
              <div>
                <label htmlFor="apiVersion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Version
                </label>
                <SelectField
                  id="apiVersion"
                  value={formData.apiVersion}
                  onChange={(e) => handleInputChange('apiVersion', e.target.value)}
                  className="w-full px-3 py-2 border border-line-strong rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="v3">v3 (Recommended)</option>
                  <option value="v2">v2</option>
                  <option value="v1">v1</option>
                </SelectField>
              </div>

              {/* Use SSL */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="useSSL"
                  checked={formData.useSSL}
                  onChange={(e) => handleInputChange('useSSL', e.target.checked)}
                  className="h-4 w-4 text-blue-600 dark:text-blue-300 focus:ring-blue-500 border-line-strong rounded"
                />
                <label htmlFor="useSSL" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  Use SSL (Recommended)
                </label>
              </div>

              {/* Timeout */}
              <div>
                <label htmlFor="timeout" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Request Timeout (seconds)
                </label>
                <input
                  type="number"
                  id="timeout"
                  value={formData.timeout / 1000}
                  onChange={(e) => handleInputChange('timeout', parseInt(e.target.value) * 1000)}
                  min="1"
                  max="300"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    validationErrors.timeout ? 'border-red-300 dark:border-red-800/50' : 'border-line-strong'
                  }`}
                />
                {validationErrors.timeout && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-300">{validationErrors.timeout}</p>
                )}
              </div>

              {/* Webhook Settings */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Webhook Settings (Optional)</h4>
                
                <div>
                  <label htmlFor="webhookEndpoint" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Webhook Endpoint URL
                  </label>
                  <input
                    type="url"
                    id="webhookEndpoint"
                    value={formData.webhookEndpoint}
                    onChange={(e) => handleInputChange('webhookEndpoint', e.target.value)}
                    placeholder="https://yourapp.com/webhooks/woocommerce"
                    className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      validationErrors.webhookEndpoint ? 'border-red-300 dark:border-red-800/50' : 'border-line-strong'
                    }`}
                  />
                  {validationErrors.webhookEndpoint && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-300">{validationErrors.webhookEndpoint}</p>
                  )}
                  <p className="mt-1 text-sm text-content-muted">
                    URL to receive real-time notifications from WooCommerce
                  </p>
                </div>

                <div>
                  <label htmlFor="webhookSecret" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Webhook Secret
                  </label>
                  <input
                    type="password"
                    id="webhookSecret"
                    value={formData.webhookSecret}
                    onChange={(e) => handleInputChange('webhookSecret', e.target.value)}
                    placeholder="Optional secret for webhook verification"
                    className="w-full px-3 py-2 border border-line-strong rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-sm text-content-muted">
                    Secret key for webhook signature verification
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Connection Test */}
        <div className="border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-content">Connection Test</h3>
            <button
              type="button"
              onClick={testConnection}
              disabled={connectionTest.status === 'testing'}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {connectionTest.status === 'testing' ? 'Testing...' : 'Test Connection'}
            </button>
          </div>

          {connectionTest.status !== 'idle' && (
            <div className={`p-4 rounded-md ${
              connectionTest.status === 'success' ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800/50' :
              connectionTest.status === 'error' ? 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50' :
              'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800/50'
            }`}>
              <div className="flex items-center">
                {connectionTest.status === 'testing' && (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600 dark:text-blue-300" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {connectionTest.status === 'success' && (
                  <svg className="mr-3 h-5 w-5 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {connectionTest.status === 'error' && (
                  <svg className="mr-3 h-5 w-5 text-red-600 dark:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                <p className={`text-sm ${
                  connectionTest.status === 'success' ? 'text-green-800 dark:text-green-300' :
                  connectionTest.status === 'error' ? 'text-red-800 dark:text-red-300' :
                  'text-blue-800 dark:text-blue-300'
                }`}>
                  {connectionTest.message}
                </p>
              </div>

              {connectionTest.details && (
                <div className="mt-2 text-sm text-content-secondary">
                  <p>WooCommerce Version: {connectionTest.details.version}</p>
                  <p>WordPress Version: {connectionTest.details.wpVersion}</p>
                  <p>PHP Version: {connectionTest.details.phpVersion}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-dark-700 rounded-md hover:bg-gray-200 dark:hover:bg-dark-600"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || connectionTest.status === 'testing'}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating Integration...' : 'Create Integration'}
          </button>
        </div>
      </form>

      {/* Setup Instructions */}
      <div className="mt-8 p-4 bg-gray-50 dark:bg-dark-700 rounded-md">
        <h4 className="text-sm font-medium text-content mb-2">Setup Instructions</h4>
        <ol className="text-sm text-content-secondary space-y-1 list-decimal list-inside">
          <li>Ensure WooCommerce plugin is installed and activated</li>
          <li>Go to WooCommerce → Settings → Advanced → REST API</li>
          <li>Click "Add Key" to create new API credentials</li>
          <li>Set permissions to "Read/Write" for full functionality</li>
          <li>Copy the generated Consumer Key and Consumer Secret</li>
          <li>Paste the credentials above and test the connection</li>
        </ol>
      </div>
    </div>
  );
};